import { useMemo } from "react";
import { Info, ChevronDown } from "lucide-react";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "src/@/components/ui/tooltip";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "src/@/components/ui/table";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "src/@/components/ui/collapsible";
import { formatCurrency } from "src/@/lib/format-currency";
import { Button } from "src/@/components/ui/button";
import { Skeleton } from "src/@/components/ui/skeleton";
import { useAppSelector } from "src/redux/hooks";
import { getUserIsSnackpassEmployee } from "src/redux/selectors";

type TableRow = {
    title: string;
    value: string;
    count?: string;
    toolTipText?: string;
    bold?: boolean;
    indented?: boolean;
};

// Create a new type that only includes the fields we need
export type SalesSummaryFields = {
    count: number;
    grossSalesAmount: number;
    refundedAmount: number;
    amountDiscounted: number;
    netSales: number;
    giftCardsSold?: number;

    storeTaxesToRemit: number;
    store3PTaxesToRemit: number;
    storeTaxesWithheld: number;
    tip: number;
    expectedCashCollected: number;
    thirdPartyPayments?: number;
    cardCollected: number;
    netCardPayments?: number;
    giftCardCredit: number;
    storeCredit: number;
    processingFee: number;
    snackpassFees: number;
    netCollected: number;
    totalCollected: number;
    totalSales: number;
    thirdPartyUnaccountedSales: number;
    unaccountedSales: number;
};

interface SalesReportProps {
    dateString: string;
    report?: SalesSummaryFields;
    topItems?: {
        name: string;
        count: number;
        value: number;
    }[];
    categoryData?: {
        name: string;
        count: number;
        value: number;
    }[];
    providerData?: {
        name: string;
        count: number;
        value: number;
    }[];
    isLoading?: {
        sales?: boolean;
        items?: boolean;
        categories?: boolean;
        providers?: boolean;
    };
}

const CollapsibleSection = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
        <Collapsible className="w-full rounded-md border h-fit">
            <CollapsibleTrigger asChild>
                <Button
                    variant="ghost"
                    className="w-full justify-between rounded-none border-b h-10 px-3"
                >
                    <span className="text-sm font-medium text-left">
                        {title}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="max-h-[300px] overflow-y-auto">
                    <Table>
                        <TableBody>{children}</TableBody>
                    </Table>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );

export const SalesSummary = ({
    dateString,
    report,
    topItems,
    categoryData,
    providerData,
    isLoading,
}: SalesReportProps) => {
    const salesRows = useSalesRows(report);
    const paymentsRows = usePaymentsRows(report);
    const categoryRows = useCategoryRows(categoryData);
    const itemRows = useItemRows(topItems);
    const providerRows = useProviderRows(providerData);
    const hasDiscrepancy = useMemo(() => {
        if (!report) return false;

        const expectedTotal =
            report.grossSalesAmount -
            report.refundedAmount -
            report.amountDiscounted;
        const actualTotal = report.netSales;

        // Check if there's more than a $0.01 difference to account for rounding
        return Math.abs(expectedTotal - actualTotal) > 0.01;
    }, [report]);

    const hasTotalSalesDiscrepancy = useMemo(() => {
        if (!report) return false;

        const expectedTotal =
            report.netSales +
            report.storeTaxesToRemit +
            report.store3PTaxesToRemit +
            report.tip;
        const actualTotal = report.totalSales;

        // Check if there's more than a $0.01 difference to account for rounding
        return Math.abs(expectedTotal - actualTotal) > 0.01;
    }, [report]);

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h4 className="text-xl">
                    <Trans>Sales Summary</Trans>
                </h4>
                <p className="text-sm text-muted-foreground">{dateString}</p>
            </div>

            {hasDiscrepancy && (
                <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 text-sm">
                    <InfoCircledIcon className="h-4 w-4 shrink-0" />
                    <p className="text-muted-foreground">
                        <Trans>
                            Reported refund and discount amounts may be affected in
                            special cases from partial refunds and special
                            discounts. We are currently fixing this — thank you for
                            your patience. Your gross, net sales and other fields
                            are accurate and not affected.
                        </Trans>
                    </p>
                </div>
            )}

            {hasTotalSalesDiscrepancy && (
                <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 text-sm">
                    <InfoCircledIcon className="h-4 w-4 shrink-0" />
                    <p className="text-muted-foreground">
                        <Trans>
                            There appears to be a discrepancy in the total sales
                            calculation. The sum of net sales, taxes, and tips does
                            not match the total sales amount. Our team is
                            investigating this issue.
                        </Trans>
                    </p>
                </div>
            )}

            <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Sales Section */}
                <div className="h-fit w-full rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead><Trans>Sales</Trans></TableHead>
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading?.sales ? (
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <Skeleton className="h-[200px]" />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                salesRows.map((row) => (
                                    <TableRow key={row.title}>
                                        <TableCell className="min-w-[200px]">
                                            <div className="flex items-center gap-1">
                                                <span
                                                    className={`truncate ${
                                                        row.bold
                                                            ? "font-medium"
                                                            : ""
                                                    } ${
                                                        row.indented
                                                            ? "pl-4"
                                                            : ""
                                                    }`}
                                                >
                                                    {row.title}
                                                </span>
                                                {row.toolTipText && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <Info className="h-3 w-3" />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p className="max-w-xs">
                                                                    {
                                                                        row.toolTipText
                                                                    }
                                                                </p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={`text-right ${
                                                row.bold ? "font-medium" : ""
                                            }`}
                                        >
                                            {row.value}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Payments Section */}
                <div className="w-full rounded-md border h-fit">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead><Trans>Payments</Trans></TableHead>
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading?.sales ? (
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <Skeleton className="h-[200px]" />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paymentsRows.map((row) => (
                                    <TableRow key={row.title}>
                                        <TableCell className="min-w-[200px]">
                                            <div className="flex items-center gap-1">
                                                <span
                                                    className={`truncate ${
                                                        row.bold
                                                            ? "font-medium"
                                                            : ""
                                                    } ${
                                                        row.indented
                                                            ? "pl-4"
                                                            : ""
                                                    }`}
                                                >
                                                    {row.title}
                                                </span>
                                                {row.toolTipText && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <Info className="h-3 w-3" />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p className="max-w-xs">
                                                                    {
                                                                        row.toolTipText
                                                                    }
                                                                </p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={`text-right ${
                                                row.bold ? "font-medium" : ""
                                            }`}
                                        >
                                            {row.value}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>


                {/* Category Sales Section */}
                <CollapsibleSection title={t`Category Sales`}>
                    <TableRow>
                        <TableHead><Trans>Category</Trans></TableHead>
                        <TableHead className="text-right"><Trans>Count</Trans></TableHead>
                        <TableHead className="text-right"><Trans>Sales</Trans></TableHead>
                    </TableRow>
                    {isLoading?.categories ? (
                        <TableRow>
                            <TableCell colSpan={3}>
                                <Skeleton className="h-[200px]" />
                            </TableCell>
                        </TableRow>
                    ) : (
                        categoryRows.map((row) => (
                            <TableRow key={row.title}>
                                <TableCell className="min-w-[200px]">
                                    {row.title}
                                </TableCell>
                                <TableCell className="text-right">
                                    {row.count}
                                </TableCell>
                                <TableCell className="text-right">
                                    {row.value}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </CollapsibleSection>

                {/* Item Sales Section */}
                <CollapsibleSection title={t`Item Sales`}>
                    <TableRow>
                        <TableHead><Trans>Item</Trans></TableHead>
                        <TableHead className="text-right"><Trans>Count</Trans></TableHead>
                        <TableHead className="text-right"><Trans>Sales</Trans></TableHead>
                    </TableRow>
                    {isLoading?.items ? (
                        <TableRow>
                            <TableCell colSpan={3}>
                                <Skeleton className="h-[200px]" />
                            </TableCell>
                        </TableRow>
                    ) : (
                        itemRows.map((row) => (
                            <TableRow key={row.title}>
                                <TableCell className="min-w-[200px]">
                                    {row.title}
                                </TableCell>
                                <TableCell className="text-right">
                                    {row.count}
                                </TableCell>
                                <TableCell className="text-right">
                                    {row.value}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </CollapsibleSection>

                {/* Provider Sales Section */}
                <CollapsibleSection title={t`Provider Sales`}>
                    <TableRow>
                        <TableHead><Trans>Provider</Trans></TableHead>
                        <TableHead className="text-right"><Trans>Count</Trans></TableHead>
                        <TableHead className="text-right"><Trans>Sales</Trans></TableHead>
                    </TableRow>
                    {isLoading?.providers ? (
                        <TableRow>
                            <TableCell colSpan={3}>
                                <Skeleton className="h-[200px]" />
                            </TableCell>
                        </TableRow>
                    ) : (
                        providerRows.map((row) => (
                            <TableRow key={row.title}>
                                <TableCell className="min-w-[200px]">
                                    {row.title}
                                </TableCell>
                                <TableCell className="text-right">
                                    {row.count}
                                </TableCell>
                                <TableCell className="text-right">
                                    {row.value}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </CollapsibleSection>
            </div>
        </div>
    );
};
const useSalesRows = function (report?: SalesSummaryFields) {
    const isSnackpassEmployee = useAppSelector(getUserIsSnackpassEmployee);
    return useMemo(
        () =>
            report
                ? [
                      {
                          title: t`Orders`,
                          value: report.count.toLocaleString(),
                          toolTipText: t`Total number of orders in this period`,
                      },
                      {
                          title: t`Gross Sales`,
                          value: formatCurrency(report.grossSalesAmount),
                          toolTipText:
                              t`Total sales before any discounts, refunds, or adjustments`,
                          bold: true,
                      },
                      {
                          title: t`Refunds`,
                          value: formatCurrency(report.refundedAmount),
                          toolTipText: t`Total amount refunded to customers, does not include tax`,
                      },
                      {
                          title: t`Discounts & Comps`,
                          value: formatCurrency(report.amountDiscounted),
                          toolTipText:
                              t`Total discounts and complimentary items given to customers`,
                      },
                      {
                          title: t`Net Sales`,
                          value: formatCurrency(report.netSales),
                          toolTipText:
                              t`Gross sales minus refunds and discounts`,
                          bold: true,
                      },
                      ...(report.giftCardsSold != null
                          ? [
                                {
                                    title: t`Gift Cards Sold (Deferred Revenue)`,
                                    value: formatCurrency(report.giftCardsSold),
                                    toolTipText: t`Gift card purchases are not counted in sales until redeemed, but they are included in payouts.`,
                                },
                            ]
                          : []),
                      {
                          title: t`Taxes`,
                          value: formatCurrency(report.storeTaxesToRemit),
                          toolTipText:
                              t`Taxes you are responsible for remitting to the state. This does not include taxes from third party orders.`,
                      },
                      // Only show taxes withheld if non-zero
                      ...(report.storeTaxesWithheld !== 0
                          ? [
                                {
                                    title: t`Taxes Withheld`,
                                    value: formatCurrency(
                                        report.storeTaxesWithheld,
                                    ),
                                    toolTipText:
                                        t`Taxes withheld from sales by the state.`,
                                },
                            ]
                          : []),
                      // Only show third party taxes if non-zero
                      ...(report.store3PTaxesToRemit !== 0
                          ? [
                                {
                                    title: t`Third Party Taxes`,
                                    value: formatCurrency(
                                        report.store3PTaxesToRemit,
                                    ),
                                    toolTipText:
                                        t`Taxes from integrated third party orders.`,
                                },
                            ]
                          : []),
                      {
                          title: t`Tips`,
                          value: formatCurrency(report.tip),
                          toolTipText: t`Total tips received from customers`,
                          indented: false,
                      },
                      {
                          title: t`Total Sales`,
                          value: formatCurrency(report.totalSales),
                          toolTipText:
                              t`Net sales plus tips and taxes collected`,
                          bold: true,
                      },
                      ...(report.thirdPartyUnaccountedSales &&
                      isSnackpassEmployee
                          ? [
                                {
                                    title: t`Uncategorized Third Party Sales [SP Only]`,
                                    value: formatCurrency(
                                        report.thirdPartyUnaccountedSales,
                                    ),
                                    toolTipText:
                                        t`Unaccounted sales from delivery integrations through Deliverect`,
                                },
                            ]
                          : []),
                      ...(report.unaccountedSales && isSnackpassEmployee
                          ? [
                                {
                                    title: t`Uncategorized Sales [SP Only]`,
                                    value: formatCurrency(
                                        report.unaccountedSales,
                                    ),
                                    toolTipText:
                                        t`Sales that don't fit into any other category`,
                                },
                            ]
                          : []),
                      // {
                      //     title: "Customer → Snackpass Fees",
                      //     value: `${formatCurrency(
                      //         report?.customerToSnackpassFees,
                      //     )}`,
                      //     toolTipText:
                      //         "Any fees charged directly to your customer by Snackpass.",
                      // },
                  ]
                : [],
        [isSnackpassEmployee, report],
    );
};

const usePaymentsRows = function (report?: SalesSummaryFields) {
    return useMemo(
        () =>
            report
                ? [
                      {
                          // TODO: ADD BACK!
                          title: t`Total Collected`,
                          //   value: formatCurrency(report?.totalCollected),
                          value: formatCurrency(report?.totalSales),
                          toolTipText: t`Your net sales plus tips and taxes.`,
                          bold: true,
                      },
                      {
                          title: t`Card`,
                          //   value: formatCurrency(report?.cardCollected),
                          value: formatCurrency(
                              report?.netCardPayments ?? report?.cardCollected,
                          ),
                          toolTipText:
                              t`Total payments received via credit/debit cards`,
                          indented: true,
                      },
                      {
                          title: t`Cash`,
                          value: formatCurrency(
                              report?.expectedCashCollected ?? 0,
                          ),
                          toolTipText:
                              t`The amount of money we expect you to have accepted in cash in your store for sales occurring in this time period.`,
                          indented: true,
                      },
                      {
                          title: t`Third Party`,
                          value: formatCurrency(
                              report?.thirdPartyPayments ?? 0,
                          ),
                          toolTipText:
                              t`Total payments received via third party processors`,
                          indented: true,
                      },
                      {
                          title: t`Gift Card Redemption`,
                          value: formatCurrency(report?.giftCardCredit ?? 0),
                          toolTipText: t`The amount of gift card redemptions`,
                          indented: true,
                      },
                      {
                          title: t`Store Credit Redemption`,
                          value: formatCurrency(report?.storeCredit ?? 0),
                          toolTipText:
                              t`The amount of store credit given to customers`,
                          indented: true,
                      },
                      {
                          title: t`Processing Fees`,
                          value: `(${formatCurrency(report?.processingFee ?? 0)})`,
                          toolTipText:
                              t`Processing fees charged by payment providers`,
                      },
                      {
                          title: t`Snackpass Fees`,
                          value: `(${formatCurrency(report?.snackpassFees)})`,
                          toolTipText:
                              t`Fees charged by Snackpass, such as commission, fax, and delivery fees.`,
                      },
                      {
                          title: t`Net Collected`,
                          //   value: formatCurrency(report?.netCollected),
                          value: formatCurrency(
                              report?.totalSales -
                                  report?.snackpassFees -
                                  report?.processingFee,
                          ),
                          toolTipText: t`Total collected minus fees.`,
                          bold: true,
                      },
                  ]
                : [],
        [report],
    );
};

const useCategoryRows = function (
    categoryData?: {
        name: string;
        count: number;
        value: number;
    }[],
) {
    return useMemo(
        () =>
            categoryData?.map((category) => ({
                title: category.name,
                count: category.count?.toLocaleString() ?? "0",
                value: formatCurrency(category.value),
            })) ?? [],
        [categoryData],
    );
};

const useItemRows = function (
    topItems?: {
        name: string;
        count: number;
        value: number;
    }[],
) {
    return useMemo(
        () =>
            topItems?.map((item) => ({
                title: item.name,
                count: item.count.toLocaleString(),
                value: formatCurrency(item.value),
            })) ?? [],
        [topItems],
    );
};

const useProviderRows = function (
    providerData?: {
        name: string;
        count: number;
        value: number;
    }[],
) {
    return useMemo(
        () =>
            providerData?.map((provider) => ({
                title: provider.name,
                count: provider.count.toLocaleString(),
                value: formatCurrency(provider.value),
            })) ?? [],
        [providerData],
    );
};
